import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Send, CalendarClock, Download } from "lucide-react";
import { api } from "@/lib/api";
import { NEWSLETTER_TEMPLATES, TEMPLATE_INSTRUMENTS, hasPlaceholders } from "@/data/newsletterTemplates";

const pct = (n, t) => (t ? `${Math.round((100 * (n || 0)) / t)}%` : "0%");
const download = (fmt) => api.get(`/admin/newsletter/export?format=${fmt}`, { responseType: "blob" }).then((r) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `mailing-list.${fmt}`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
}).catch(() => toast.error("Couldn't download the list"));

const AUDIENCES = [["all", "Everyone"], ["piano", "Piano"], ["guitar", "Guitar"], ["drums", "Drums"], ["voice", "Voice"]];
const LABEL = { piano: "Piano", guitar: "Guitar", drums: "Drums", voice: "Voice", all: "Everything" };
const SOURCES = { footer: "Footer box", page: "Keep in touch page", checkout: "Course checkout", register: "Sign-up page" };
const inp = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm";
const day = (s) => s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
const tomorrow = () => new Date(Date.now() + 86400000).toLocaleDateString("en-CA", { timeZone: "Europe/London" });

const Composer = ({ counts, onSent }) => {
  const [m, setM] = useState({ subject: "", body: "", audience: "all", button_label: "", button_url: "", send_on: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setM({ ...m, [k]: e.target.value });
  const applyTemplate = (e) => {
    const t = NEWSLETTER_TEMPLATES.find((x) => x.id === e.target.value);
    if (t && (!m.body || window.confirm("Replace what you've written with this template?"))) setM({ ...m, subject: t.subject, body: t.body, audience: t.audience, button_label: t.button_label, button_url: t.button_url });
    e.target.value = "";
  };
  const go = async (test) => {
    if (!test && hasPlaceholders(`${m.subject} ${m.body} ${m.button_label} ${m.button_url}`)) { toast.error("Fill in the parts in [square brackets] before sending"); return; }
    const ask = m.send_on ? `Schedule "${m.subject}" for 9am UK time on ${day(m.send_on)}?` : `Send "${m.subject}" to ${counts[m.audience]} subscriber(s) now?`;
    if (!test && !window.confirm(ask)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { ...m, send_on: m.send_on || null, test });
      toast.success(test ? `Test sent to ${data.to}` : data.scheduled ? `Scheduled for 9am on ${day(data.scheduled)}` : `Sending to ${data.total} subscriber(s)`);
      if (!test) { setM({ subject: "", body: "", audience: "all", button_label: "", button_url: "", send_on: "" }); onSent(); }
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Please fill in a subject and message"); }
    setBusy(false);
  };
  return (
    <div className="mt-4 grid gap-3" data-testid="newsletter-composer">
      <select defaultValue="" onChange={applyTemplate} className={inp} data-testid="newsletter-template">
        <option value="">Start from a ready-made template…</option>
        {TEMPLATE_INSTRUMENTS.map(([k, name]) => (
          <optgroup key={k} label={name}>{NEWSLETTER_TEMPLATES.filter((t) => t.audience === k).map((t) => <option key={t.id} value={t.id}>{name}: {t.type}</option>)}</optgroup>
        ))}
      </select>
      {hasPlaceholders(`${m.subject} ${m.body} ${m.button_label} ${m.button_url}`) && <p className="text-xs text-primary" data-testid="newsletter-placeholder-warning">Fill in the parts in [square brackets] before sending.</p>}
      <input value={m.subject} onChange={set("subject")} placeholder="Subject" className={inp} data-testid="newsletter-subject" />
      <textarea value={m.body} onChange={set("body")} rows={7} placeholder={"Your message. Leave a blank line between paragraphs.\n(“Hi [name],” is added automatically.)"} className={inp} data-testid="newsletter-body" />
      <div className="grid gap-3 sm:grid-cols-3">
        <select value={m.audience} onChange={set("audience")} className={inp} data-testid="newsletter-audience">
          {AUDIENCES.map(([v, l]) => <option key={v} value={v}>{l} ({counts[v]})</option>)}
        </select>
        <input value={m.button_label} onChange={set("button_label")} placeholder="Button text (optional)" className={inp} data-testid="newsletter-button-label" />
        <input value={m.button_url} onChange={set("button_url")} placeholder="Button link https://… (optional)" className={inp} data-testid="newsletter-button-url" />
      </div>
      <label className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <CalendarClock size={15} /> Send later (9am UK time) on
        <input type="date" min={tomorrow()} value={m.send_on} onChange={set("send_on")} className="rounded-xl border border-border bg-background px-3 py-1.5 text-sm text-foreground" data-testid="newsletter-send-on" />
        {m.send_on && <button type="button" onClick={() => setM({ ...m, send_on: "" })} className="text-xs underline" data-testid="newsletter-send-on-clear">Send now instead</button>}
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={() => go(true)} className="rounded-full border border-border px-4 py-2 text-sm font-semibold" data-testid="newsletter-send-test">Send me a test</button>
        <button type="button" disabled={busy || (!m.send_on && !counts[m.audience])} onClick={() => go(false)} className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50" data-testid="newsletter-send">
          {m.send_on ? <><CalendarClock size={14} /> Schedule for {day(m.send_on)}</> : <><Send size={14} /> Send to subscribers now</>}
        </button>
      </div>
    </div>
  );
};

const SourceStats = ({ subs }) => {
  const by = {};
  subs.filter((s) => s.status !== "unsubscribed").forEach((s) => {
    const src = s.source || "page";
    by[src] = by[src] || { total: 0, pages: {} };
    by[src].total += 1;
    const p = s.page || "—";
    by[src].pages[p] = (by[src].pages[p] || 0) + 1;
  });
  const rows = Object.entries(by).sort((a, b) => b[1].total - a[1].total);
  return (
    <div className="mt-6" data-testid="newsletter-sources">
      <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Where sign-ups come from</h3>
      {rows.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No sign-ups yet.</p> : (
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">{rows.map(([src, v]) => (
          <li key={src} className="rounded-xl border border-border p-3 text-sm" data-testid={`newsletter-source-${src}`}>
            <p className="flex justify-between font-semibold"><span>{SOURCES[src] || src}</span><span className="text-primary" data-testid={`newsletter-source-${src}-count`}>{v.total}</span></p>
            <p className="mt-1 text-xs text-muted-foreground">{Object.entries(v.pages).sort((a, b) => b[1] - a[1]).map(([p, n]) => `${p} ${n}`).join(" · ")}</p>
          </li>
        ))}</ul>
      )}
      <p className="mt-1 text-xs text-muted-foreground">Confirmed and awaiting-confirmation sign-ups, by the page they were on.</p>
    </div>
  );
};

const SendsList = ({ sends, reload }) => {
  const cancel = (s) => window.confirm(`Cancel "${s.subject}"?`) && api.delete(`/admin/newsletter/sends/${s.id}`).then(() => { toast.success("Cancelled"); reload(); })
    .catch((e) => toast.error(e.response?.data?.detail || "Couldn't cancel"));
  if (!sends.length) return null;
  return (
    <div className="mt-6" data-testid="newsletter-sends">
      <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Scheduled &amp; sent</h3>
      <ul className="mt-2 divide-y divide-border text-sm">{sends.map((s) => (
        <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid="newsletter-send-item">
          <span className="font-semibold">{s.subject}</span>
          <span className="flex items-center gap-2 text-muted-foreground">
            {s.audience === "all" ? "Everyone" : LABEL[s.audience]} ·{" "}
            {s.status === "scheduled" ? <span className="text-primary" data-testid="newsletter-send-scheduled">Scheduled for 9am, {day(s.send_on)}</span>
              : <>{s.sent}/{s.total} sent{s.failed ? ` · ${s.failed} failed` : ""} · <span data-testid={`newsletter-opens-${s.id}`}>{s.opens || 0} opened ({pct(s.opens, s.sent)})</span> · <span data-testid={`newsletter-clicks-${s.id}`}>{s.clicks || 0} clicked ({pct(s.clicks, s.sent)})</span> · {day(s.started_at || s.created_at)}</>}
            {s.status === "scheduled" && <button type="button" onClick={() => cancel(s)} className="text-xs underline hover:text-destructive" data-testid={`newsletter-cancel-${s.id}`}>Cancel</button>}
          </span>
        </li>
      ))}</ul>
      <p className="mt-2 text-xs text-muted-foreground" data-testid="newsletter-accuracy-note">Opens are a rough guide: iPhone Mail and some other apps load emails automatically, so they can count as opened when they weren't, and people who block images aren't counted. Button clicks are the reliable number.</p>
    </div>
  );
};

export const AdminNewsletter = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/newsletter").then((r) => setD(r.data)).catch(() => setD({ subscribers: [], sends: [] }));
  useEffect(() => { load(); }, []);
  const sending = d?.sends?.some((s) => s.status === "sending");
  useEffect(() => {
    if (!sending) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [sending]);
  if (!d) return <p className="mt-10 text-sm" data-testid="admin-newsletter-loading">Loading mailing list…</p>;
  const confirmed = d.subscribers.filter((s) => s.status === "confirmed");
  const counts = Object.fromEntries(AUDIENCES.map(([v]) => [v, v === "all" ? confirmed.length : confirmed.filter((s) => s.instrument === v || s.instrument === "all").length]));
  const remove = (s) => window.confirm(`Remove ${s.email}?`) && api.delete(`/admin/newsletter/${s.id}`).then(() => { toast.success("Removed"); load(); });
  return (
    <section className="mt-10 rounded-2xl border border-border bg-card p-5" data-testid="admin-newsletter">
      <h2 className="font-serif text-2xl">Mailing list</h2>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={() => download("xlsx")} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary" data-testid="newsletter-export-xlsx"><Download size={13} /> Download Excel</button>
        <button type="button" onClick={() => download("csv")} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary" data-testid="newsletter-export-csv"><Download size={13} /> Download CSV</button>
      </div>
      <p className="mt-1 text-sm text-muted-foreground" data-testid="admin-newsletter-counts">{confirmed.length} confirmed · {d.subscribers.filter((s) => s.status === "pending").length} awaiting confirmation · {d.subscribers.filter((s) => s.status === "unsubscribed").length} unsubscribed</p>
      <Composer counts={counts} onSent={() => setTimeout(load, 1500)} />
      <SendsList sends={d.sends} reload={load} />
      <SourceStats subs={d.subscribers} />
      <details className="mt-6" data-testid="newsletter-subscribers">
        <summary className="cursor-pointer text-sm font-semibold">Subscribers ({d.subscribers.length})</summary>
        <ul className="mt-2 max-h-80 divide-y divide-border overflow-y-auto text-sm">{d.subscribers.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-2 py-2" data-testid="newsletter-subscriber">
            <span>{s.name} <span className="text-muted-foreground">({s.email}) · {LABEL[s.instrument]} · {s.status} · {SOURCES[s.source] || "—"}{s.page ? ` (${s.page})` : ""} · {day(s.created_at)}</span></span>
            <button type="button" onClick={() => remove(s)} aria-label={`Remove ${s.email}`} className="text-muted-foreground hover:text-destructive" data-testid={`newsletter-remove-${s.id}`}><Trash2 size={14} /></button>
          </li>
        ))}</ul>
      </details>
    </section>
  );
};
